import { useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { salesForTier, priceFrom, DEFAULT_PREFS } from '../../utils/priceMath';
import { palette, money2, relativeAge } from './pricingTokens';
import haptics from '../../utils/haptics';

/**
 * Price history for whichever row of the ladder is selected.
 *
 * Nothing is fetched. Every sale already sits in `evidence` with a date, a
 * price and an anomaly flag, which is the same array the deep-dive sheet opens.
 *
 * **Every number here comes out of `priceFrom`.** That matters more than it
 * sounds: this component used to compute its own headline two different ways -
 * the last point of a smoothed line in the small chart, a quarter-of-the-window
 * median in the big one - and the ladder computed a third. Charizard VSTAR
 * showed $1,512, $1,486 and $1,475 for the same card on one screen. There is
 * one rule in this app, the average of the last three clean sales, it lives in
 * priceMath, and nothing is allowed to invent another.
 *
 * The line is still a rolling median, because a line is a shape rather than a
 * number and one bad comp should not put a cliff in it. The headline is never
 * read off the line.
 */

export const CHART_WINDOWS = [
  { days: 30, label: '30 days' },
  { days: 60, label: '60 days' },
  { days: 90, label: '90 days' },
  { days: 365, label: '1 year' },
  { days: 0, label: 'All time' },
];

/** Below this a window is not a market, it is an anecdote. */
const MIN_POINTS = 3;
/** Grey dots past this stop being data and become a smear. */
const DOT_CAP = 18;
/** Windows the resting chart climbs through when 30 days is too thin to draw. */
const FALLBACK_DAYS = [30, 90, 365, 0];

const DAY = 86400000;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const median = (nums) => {
  if (!nums.length) return null;
  const s = [...nums].sort((a, b) => a - b);
  const m = s.length >> 1;
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
};

/**
 * Sales for one tier, dated, oldest first, each carrying the original row.
 * The original matters: `priceFrom` needs the whole sale to decide whether it
 * counts, not just its price.
 */
function timeline(evidence, tierKey) {
  return (salesForTier(evidence, tierKey) || [])
    .filter((s) => s?.date && s?.price > 0)
    .map((s) => ({ t: Date.parse(s.date), p: Number(s.price), odd: Boolean(s.anomalyFlag), sale: s }))
    .filter((s) => Number.isFinite(s.t))
    .sort((a, b) => a.t - b.t);
}

const within = (pts, days) => {
  if (!days) return pts;
  const cut = Date.now() - days * DAY;
  return pts.filter((s) => s.t >= cut);
};

/**
 * The one number, for a set of points.
 *
 * `priceFrom` wants newest first and applies the same hold-out rules the card
 * face uses, so a flagged sale is excluded here exactly as it is everywhere
 * else. Given a window containing the three most recent sales, this returns the
 * ladder's number to the cent.
 */
function headlineFor(pts) {
  if (!pts.length) return { value: null, counted: 0 };
  return priceFrom([...pts].reverse().map((x) => x.sale), DEFAULT_PREFS);
}

/** A rolling median, so one returned sale does not become a market event. */
function smooth(pts) {
  const win = Math.max(1, Math.round(pts.length / 6));
  return pts.map((s, i) => ({
    t: s.t,
    p: median(pts.slice(Math.max(0, i - win), Math.min(pts.length, i + win + 1)).map((x) => x.p)),
  }));
}

/**
 * Which sales are worth drawing: every flagged one, the high, the low, the
 * newest, then an even sample of the rest.
 */
function relevantDots(pts, cap = DOT_CAP) {
  if (pts.length <= cap) return pts;

  const keep = new Set();
  let lo = 0;
  let hi = 0;
  pts.forEach((s, i) => {
    if (s.p < pts[lo].p) lo = i;
    if (s.p > pts[hi].p) hi = i;
    if (s.odd) keep.add(i);
  });
  keep.add(lo);
  keep.add(hi);
  keep.add(0);
  keep.add(pts.length - 1);

  const room = Math.max(0, cap - keep.size);
  const step = pts.length / Math.max(1, room);
  for (let k = 0; k < room; k += 1) keep.add(Math.round(k * step));

  return [...keep].filter((i) => i >= 0 && i < pts.length).sort((a, b) => a - b).map((i) => pts[i]);
}

const dayLabel = (ms, wide) => {
  const d = new Date(ms);
  return wide
    ? `${MONTHS[d.getMonth()]} '${String(d.getFullYear()).slice(2)}`
    : `${MONTHS[d.getMonth()]} ${d.getDate()}`;
};

const fullDate = (ms) => {
  const d = new Date(ms);
  return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
};

const windowWord = (days) => CHART_WINDOWS.find((w) => w.days === days)?.label || `${days} days`;

export default function PriceHistory({
  evidence, tierKey, tierLabel, theme, isDark = true, open, onToggle, pending,
  hasPrice = false,
}) {
  const c = palette(theme, isDark);
  const s = styles(c);
  const [days, setDays] = useState(30);
  const [picked, setPicked] = useState(null);

  const all = useMemo(() => timeline(evidence, tierKey), [evidence, tierKey]);

  /**
   * The resting chart shows 30 days, unless 30 days holds fewer than three
   * sales. Widening rather than drawing a confident line through two dots also
   * keeps this number equal to the ladder's, because both then average the same
   * three most recent sales.
   */
  const resting = useMemo(() => {
    for (const d of FALLBACK_DAYS) {
      const pts = within(all, d);
      if (pts.length >= MIN_POINTS || d === 0) return { days: d, pts };
    }
    return { days: 30, pts: [] };
  }, [all]);

  const expanded = useMemo(() => within(all, days), [all, days]);
  const restHead = useMemo(() => headlineFor(resting.pts), [resting.pts]);
  const openHead = useMemo(() => headlineFor(expanded), [expanded]);

  if (pending) {
    return (
      <div style={s.card}>
        <div style={s.label}>Price history</div>
        <div style={{ ...s.skeleton, marginTop: '8px' }} />
        <div style={{ ...s.skeleton, width: '100%', height: '30px', marginTop: '8px' }} />
      </div>
    );
  }

  // No sales to chart is a real answer and has to look like one - but it must
  // not contradict the ladder. A card can have a cached average with no
  // individual sales kept behind it, and saying "no sales on record" directly
  // above a row reading $11.40 makes both look wrong.
  if (!all.length) {
    return (
      <div style={{ ...s.card, cursor: 'default' }}>
        <div style={s.label}>{tierLabel || 'Price'} · history</div>
        <div style={s.emptyTitle}>
          {hasPrice ? 'No history yet' : 'No sales on record'}
        </div>
        <div style={s.emptyBody}>
          {hasPrice
            ? 'The prices below are cached averages. Individual sales have not been kept for this one.'
            : `Nothing recorded for ${tierLabel || 'this tier'} yet.`}
        </div>
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        style={{ ...s.card, ...(open ? s.cardOpen : {}) }}
        onClick={() => { haptics.tap(); onToggle(); }}
        aria-expanded={open}
      >
        {/* Says what the number is, not just how many days. "30d" told nobody
            which of the three numbers on this screen they were looking at. */}
        <span style={s.label}>{tierLabel || 'Price'} · avg last 3 sold</span>
        <span style={s.row}>
          <span style={s.price}>{restHead.value != null ? money2(restHead.value) : '--'}</span>
          <Delta pts={resting.pts} c={c} s={s} />
        </span>
        <span style={s.sub}>{windowWord(resting.days)}</span>
        <Spark pts={resting.pts} c={c} />
      </button>

      {open && (
        <div style={s.scrim} onClick={onToggle} role="presentation">
          <div
            style={s.modal}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label={`${tierLabel} price history`}
          >
            <div style={s.head}>
              <div>
                <div style={s.headTier}>{tierLabel} · eBay sold</div>
                <div style={s.headPrice}>{openHead.value != null ? money2(openHead.value) : '--'}</div>
                <div style={s.headSub}>
                  Average of the last {openHead.counted || 0} sold · {windowWord(days)}
                </div>
              </div>
              <button
                type="button"
                style={s.close}
                onClick={() => { haptics.tap(); onToggle(); }}
                aria-label="Close price history"
              >
                <X size={16} />
              </button>
            </div>

            <Plot
              pts={expanded}
              days={days}
              c={c}
              picked={picked}
              onPick={(p) => { haptics.tap(); setPicked(p); }}
            />

            {/* Tapping a dot names that sale. Without it the dots are decoration
                and "which sale is that" has no answer. */}
            <div style={s.readout}>
              {picked ? (
                <>
                  <span style={s.readoutPrice}>{money2(picked.p)}</span>
                  <span style={s.readoutDate}>
                    {fullDate(picked.t)} · {relativeAge(new Date(picked.t).toISOString())}
                  </span>
                  {picked.odd && <span style={s.readoutFlag}>flagged, not counted</span>}
                </>
              ) : (
                <span style={s.readoutHint}>
                  {expanded.length ? 'Tap a sale to see its date' : 'No sales in this range'}
                </span>
              )}
            </div>

            <div style={s.ranges} role="group" aria-label="Time range">
              {CHART_WINDOWS.map((w) => (
                <button
                  key={w.days}
                  type="button"
                  style={{ ...s.range, ...(days === w.days ? s.rangeOn : {}) }}
                  aria-pressed={days === w.days}
                  onClick={() => { haptics.tap(); setDays(w.days); setPicked(null); }}
                >
                  {w.days === 365 ? '1Y' : w.days === 0 ? 'All' : `${w.days}d`}
                </button>
              ))}
            </div>

            <div style={s.footnote}>
              {expanded.length} sale{expanded.length === 1 ? '' : 's'} in this range
              {expanded.some((p) => p.odd)
                && ` · ${expanded.filter((p) => p.odd).length} flagged and held out`}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------- pieces ----

/** Direction of travel across the window. A shape, never the headline. */
function Delta({ pts, c, s }) {
  if (pts.length < 2) return <span style={{ ...s.delta, color: c.secondary }}>thin</span>;
  const clean = pts.filter((p) => !p.odd);
  const line = smooth(clean.length >= MIN_POINTS ? clean : pts);
  const ch = ((line[line.length - 1].p - line[0].p) / line[0].p) * 100;
  return (
    <span style={{ ...s.delta, color: ch > 1.5 ? c.green : ch < -1.5 ? c.red : c.secondary }}>
      {ch >= 0 ? '+' : ''}{ch.toFixed(1)}%
    </span>
  );
}

/** The resting chart. A line and the endpoint. No axis, no scatter. */
function Spark({ pts, c }) {
  if (pts.length < 2) return <div style={{ height: '30px' }} />;

  const clean = pts.filter((p) => !p.odd);
  const line = smooth(clean.length >= MIN_POINTS ? clean : pts);
  const vals = line.map((p) => p.p);
  const lo = Math.min(...vals);
  const hi = Math.max(...vals);
  const rng = hi - lo || hi * 0.06;
  const t0 = line[0].t;
  const span = Math.max(1, line[line.length - 1].t - t0);
  const X = (t) => 2 + ((t - t0) / span) * 146;
  const Y = (p) => 27 - ((p - lo + rng * 0.12) / (rng * 1.24)) * 23;

  const d = line.map((p, i) => `${i ? 'L' : 'M'}${X(p.t).toFixed(1)} ${Y(p.p).toFixed(1)}`).join(' ');
  const last = line[line.length - 1];
  const col = last.p >= line[0].p ? c.green : c.red;
  const id = `spk${Math.round(lo)}${line.length}`;

  return (
    <svg viewBox="0 0 150 30" preserveAspectRatio="none" style={{ width: '100%', height: '30px', marginTop: '4px', display: 'block' }} aria-hidden="true">
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={col} stopOpacity="0.22" />
          <stop offset="1" stopColor={col} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={`${d} L${X(last.t).toFixed(1)} 30 L${X(t0).toFixed(1)} 30 Z`} fill={`url(#${id})`} />
      <path d={d} fill="none" stroke={col} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={X(last.t).toFixed(1)} cy={Y(last.p).toFixed(1)} r="2.4" fill={col} stroke={c.card} strokeWidth="1.2" />
    </svg>
  );
}

/** The opened chart. Grid, trend line, tappable sales, a dated axis. */
function Plot({ pts, days, c, picked, onPick }) {
  const W = 320;
  const H = 156;
  const PADT = 10;
  const PADB = 20;
  const PADX = 8;

  if (pts.length < 2) {
    return (
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: '156px', display: 'block', margin: '14px 0 2px' }} role="img" aria-label="Not enough sales to chart">
        <text x={W / 2} y={H / 2} textAnchor="middle" fill={c.faint} fontSize="11">
          Not enough sales in this range
        </text>
      </svg>
    );
  }

  const clean = pts.filter((p) => !p.odd);
  const line = smooth(clean.length >= MIN_POINTS ? clean : pts);
  const vals = pts.map((p) => p.p);
  let lo = Math.min(...vals);
  let hi = Math.max(...vals);
  const pad = (hi - lo) * 0.18 || hi * 0.1;
  lo -= pad;
  hi += pad;

  const t0 = pts[0].t;
  const t1 = Date.now();
  const span = Math.max(1, t1 - t0);
  const X = (t) => PADX + ((t - t0) / span) * (W - PADX * 2);
  const Y = (p) => PADT + (1 - (p - lo) / (hi - lo)) * (H - PADT - PADB);

  const d = line.map((p, i) => `${i ? 'L' : 'M'}${X(p.t).toFixed(1)} ${Y(p.p).toFixed(1)}`).join(' ');
  const base = H - PADB;
  const last = line[line.length - 1];
  const wide = days === 0 || days > 300;

  // Three dated ticks rather than two endpoints. An axis reading only "Jul 10"
  // and "Aug 26" leaves the whole middle of the chart undated.
  const ticks = [0, 0.5, 1].map((f) => t0 + f * span);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: '156px', display: 'block', margin: '14px 0 2px' }} role="img" aria-label={`${pts.length} sales plotted over time`}>
      <defs>
        <linearGradient id="phFade" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={c.blue} stopOpacity="0.2" />
          <stop offset="1" stopColor={c.blue} stopOpacity="0" />
        </linearGradient>
      </defs>

      {[0, 0.5, 1].map((f) => (
        <line key={f} x1="0" x2={W} y1={PADT + f * (H - PADT - PADB)} y2={PADT + f * (H - PADT - PADB)} stroke={c.divider} strokeWidth="1" />
      ))}

      <path d={`${d} L${X(last.t).toFixed(1)} ${base} L${X(line[0].t).toFixed(1)} ${base} Z`} fill="url(#phFade)" />
      <path d={d} fill="none" stroke={c.blue} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />

      {relevantDots(pts).map((p, i) => {
        const on = picked && picked.t === p.t && picked.p === p.p;
        return (
          <g key={`${p.t}-${i}`} onClick={() => onPick(p)} style={{ cursor: 'pointer' }}>
            {/* A 2px dot is not a tap target. The invisible disc is. */}
            <circle cx={X(p.t)} cy={Y(p.p)} r="11" fill="transparent" />
            <circle
              cx={X(p.t).toFixed(1)}
              cy={Y(p.p).toFixed(1)}
              r={on ? 4.4 : p.odd ? 2.7 : 2.1}
              fill={on ? c.white : p.odd ? 'none' : c.secondary}
              stroke={p.odd ? c.holo : 'none'}
              strokeWidth={p.odd ? 1.2 : 0}
              opacity={on ? 1 : p.odd ? 0.95 : 0.5}
            />
          </g>
        );
      })}

      <circle cx={X(last.t).toFixed(1)} cy={Y(last.p).toFixed(1)} r="5.5" fill={c.blue} opacity="0.18" />
      <circle cx={X(last.t).toFixed(1)} cy={Y(last.p).toFixed(1)} r="2.9" fill={c.blue} stroke={c.sheet} strokeWidth="1.4" />

      {ticks.map((t, i) => (
        <text
          key={t}
          x={i === 0 ? PADX : i === 2 ? W - PADX : W / 2}
          y={H - 5}
          fill={c.faint}
          fontSize="9.5"
          textAnchor={i === 0 ? 'start' : i === 2 ? 'end' : 'middle'}
        >
          {dayLabel(t, wide)}
        </text>
      ))}
    </svg>
  );
}

const styles = (c) => ({
  card: {
    display: 'flex', flexDirection: 'column', gap: '1px', width: '100%',
    background: c.card, border: `0.5px solid ${c.hairline}`, borderRadius: '13px',
    padding: '10px 11px 8px', cursor: 'pointer', textAlign: 'left', color: 'inherit',
    fontFamily: 'inherit', WebkitTapHighlightColor: 'transparent',
  },
  cardOpen: { borderColor: c.blue, background: c.lifted },
  label: {
    fontSize: '9.5px', fontWeight: '600', letterSpacing: '0.07em',
    textTransform: 'uppercase', color: c.muted,
  },
  row: { display: 'flex', alignItems: 'baseline', gap: '6px' },
  price: {
    fontSize: '19px', fontWeight: '600', color: c.white, letterSpacing: '-0.02em',
    fontVariantNumeric: 'tabular-nums', lineHeight: 1.15,
  },
  delta: { fontSize: '11.5px', fontWeight: '500', fontVariantNumeric: 'tabular-nums' },
  sub: { fontSize: '10px', color: c.faint },

  // Centred, not a bottom sheet. The chart is the thing being looked at, so it
  // sits where the eye already is rather than under the thumb.
  scrim: {
    position: 'fixed', inset: 0, zIndex: 890, background: 'rgba(0, 0, 0, 0.62)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
    animation: 'cc-fade 0.18s ease both',
  },
  modal: {
    width: '100%', maxWidth: '380px', background: c.sheet,
    border: `0.5px solid ${c.hairline}`, borderRadius: '18px', padding: '16px 16px 14px',
    boxShadow: '0 30px 70px -20px rgba(0, 0, 0, 0.7)',
    animation: 'cc-pop 0.2s cubic-bezier(0.2, 0.8, 0.3, 1) both',
  },
  head: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '10px' },
  headTier: { fontSize: '11px', color: c.muted, letterSpacing: '0.04em' },
  headPrice: {
    fontSize: '26px', fontWeight: '600', color: c.white, letterSpacing: '-0.02em',
    fontVariantNumeric: 'tabular-nums', lineHeight: 1.12, marginTop: '3px',
  },
  headSub: { fontSize: '11.5px', color: c.faint, marginTop: '3px' },
  close: {
    border: 0, background: c.lifted, color: c.secondary, width: '30px', height: '30px',
    borderRadius: '50%', display: 'inline-flex', alignItems: 'center',
    justifyContent: 'center', cursor: 'pointer', flex: 'none',
    WebkitTapHighlightColor: 'transparent',
  },

  readout: {
    display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap',
    minHeight: '20px', padding: '2px 2px 10px',
  },
  readoutPrice: {
    fontSize: '14px', fontWeight: '600', color: c.white, fontVariantNumeric: 'tabular-nums',
  },
  readoutDate: { fontSize: '11.5px', color: c.secondary },
  readoutFlag: { fontSize: '10px', color: c.holo },
  readoutHint: { fontSize: '11.5px', color: c.faint },

  ranges: { display: 'flex', gap: '5px', borderTop: `0.5px solid ${c.divider}`, paddingTop: '10px' },
  range: {
    flex: 1, border: '0.5px solid transparent', background: 'transparent', color: c.secondary,
    fontFamily: 'inherit', fontSize: '12px', fontWeight: '500', padding: '8px 0',
    borderRadius: '8px', cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
  },
  rangeOn: { background: c.blueTint, borderColor: c.blue, color: c.blue },

  footnote: { fontSize: '10.5px', color: c.faint, textAlign: 'center', marginTop: '10px' },

  emptyTitle: {
    fontSize: '15px', fontWeight: '600', color: c.secondary, marginTop: '4px',
  },
  emptyBody: { fontSize: '11px', color: c.faint, marginTop: '2px', lineHeight: 1.4 },

  skeleton: {
    display: 'block', width: '70%', height: '15px', borderRadius: '5px',
    background: c.chipBorder, opacity: 0.8, animation: 'cc-pulse 1.4s ease-in-out infinite',
  },
});
